import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { GroupType, hasRole } from "@greenflagdate/shared";
import { useLocation } from "react-router-dom";
import type { IconName } from "../components/Icon/Icon";
import { useStoreData } from "./store/store";

/**
 * The staff side of Green Flag someone can switch to: admins get the admin
 * workspace, hosts who are not admins get check-in.
 */
export type Workspace = "admin" | "host";

/** Either side of the ModeSwitch: the member app, or a workspace. */
export type Mode = "app" | Workspace;

/**
 * Everything the chrome says about a mode: its name, its icon, and the page it
 * opens on when there is no last page to go back to.
 */
export const MODES: Record<Mode, { label: string; icon: IconName; home: string }> = {
  app: { label: "App", icon: "heart", home: "/events" },
  admin: { label: "Admin", icon: "sliders", home: "/admin/events" },
  host: { label: "Check-in", icon: "circle-check", home: "/host" },
};

/**
 * The workspace this person has, if any. Hosts get theirs on the web only:
 * the native app has no /host route, and no camera permission for the
 * scanner.
 */
export function useWorkspace(): Workspace | null {
  const { user } = useStoreData();
  if (hasRole(user, GroupType.Admin)) return "admin";
  if (hasRole(user, GroupType.Host) && !Capacitor.isNativePlatform()) {
    return "host";
  }
  return null;
}

/**
 * Per person, so another account signed in on the same tab (a shared phone at
 * the door, the dev account switcher) never inherits someone else's pages.
 */
const storageKey = (userId: number, mode: Mode) =>
  `GF_LAST_PATH_${userId}_${mode.toUpperCase()}`;

/**
 * Where the ModeSwitch sends someone: the page they were last on in that mode
 * this session, else the mode's home. With nobody signed in, or storage
 * missing or throwing (a private window), it is the home.
 */
export function lastPath(userId: number | undefined, mode: Mode): string {
  if (userId === undefined) return MODES[mode].home;
  try {
    return (
      window.sessionStorage.getItem(storageKey(userId, mode)) ??
      MODES[mode].home
    );
  } catch {
    return MODES[mode].home;
  }
}

const WORKSPACE_PAGE = /^\/(admin|host)(\/|$)/;
/**
 * Pages switching back must never land on. Settings opens as an overlay over
 * another page. The partner portal is neither side. Sign-in, an invitation
 * and a registration claim are one-shot screens with no nav to leave by, and
 * a scanned check-in code checks its attendee in again when it is reopened.
 */
const NOT_REMEMBERED =
  /^\/(settings|partner|sign-in|invite|register|host\/check-in)(\/|$)/;

/**
 * The side of the ModeSwitch a page is on, for someone with `workspace`; null
 * when the page is not remembered. An admin's check-in pages belong to the
 * admin workspace, and a host has no admin pages.
 */
export function modeOf(
  pathname: string,
  workspace: Workspace | null,
): Mode | null {
  if (!workspace || NOT_REMEMBERED.test(pathname)) return null;
  if (!WORKSPACE_PAGE.test(pathname)) return "app";
  if (pathname.startsWith("/admin") && workspace !== "admin") return null;
  return workspace;
}

/**
 * Remembers the current page as the last one on its side of the ModeSwitch.
 * Mounted once, by Page, so the side comes from the URL alone and never from
 * which chrome happens to be on screen.
 */
export function useRememberWorkspacePath() {
  const userId = useStoreData().user?.id;
  const workspace = useWorkspace();
  const { pathname, search } = useLocation();

  useEffect(() => {
    const mode = modeOf(pathname, workspace);
    if (userId === undefined || !mode) return;
    try {
      window.sessionStorage.setItem(
        storageKey(userId, mode),
        pathname + search,
      );
    } catch {
      // Nothing to remember into; the switch falls back to the home.
    }
  }, [userId, workspace, pathname, search]);
}
